import Button from '@mui/material/Button';
import { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge } from '@mui/material';
import { SALES_REPORTS, VAT_REPORTS } from 'redux/reducers/reports/constants';
import { TGetAvailableStandardVatReportTypesResponseData } from 'redux/reducers/auth/types';
import { PATHS } from 'routes/constants';
import { NAVIGATE, NAVIGATE_ITEMS } from '../constants';

export interface TNavEntry {
	name: string;
	path: string;
	badge: string | null;
	// Extra paths (e.g. sub pages) on which the entry is highlighted as active.
	activePaths?: string[];
}

interface GetNavEntriesParams {
	availableVatReportTypes: TGetAvailableStandardVatReportTypesResponseData;
	availableStandardVatReportTypes: TGetAvailableStandardVatReportTypesResponseData;
	isShipmentsEnabled: boolean;
	isEuDdpEnabled: boolean;
}

export const getNavEntries = ({
	availableVatReportTypes,
	availableStandardVatReportTypes,
	isShipmentsEnabled,
	isEuDdpEnabled,
}: GetNavEntriesParams): TNavEntry[] =>
	NAVIGATE.reduce<TNavEntry[]>((acc, item) => {
		const { name, badge, activePaths } = item as typeof item & { activePaths?: string[] };
		let { path } = item;

		if (name === NAVIGATE_ITEMS.VAT_REPORTS) {
			if (!availableVatReportTypes || availableVatReportTypes.length === 0) return acc;
			path = path?.replace(
				':typeOfReport',
				VAT_REPORTS.find((report) => (availableVatReportTypes?.[0] || '') === report.type)?.path ||
					'',
			);
		}

		if (name === NAVIGATE_ITEMS.SALES_REPORTS) {
			if (!availableStandardVatReportTypes || availableStandardVatReportTypes.length === 0)
				return acc;
			path = path?.replace(
				':typeOfReport',
				SALES_REPORTS.find((report) => (availableStandardVatReportTypes?.[0] || '') === report.type)
					?.path || '',
			);
		}

		if (name === NAVIGATE_ITEMS.SHIPMENTS && !isShipmentsEnabled) return acc;
		if (name === NAVIGATE_ITEMS.EU_DDP_SHIPMENTS && !isEuDdpEnabled) return acc;

		acc.push({ name, path, badge, activePaths });
		return acc;
	}, []);

const isPathActive = (pathname: string, path: string): boolean =>
	pathname.split(PATHS.HOME_PAGE)[1] === path.split(PATHS.HOME_PAGE)[1] ||
	pathname.split(PATHS.HOME_PAGE)[1] === path.split(PATHS.HOME_PAGE)[1].slice(0, -1);

export const isNavEntryActive = (pathname: string, entry: TNavEntry): boolean =>
	[entry.path, ...(entry.activePaths ?? [])].some((path) => isPathActive(pathname, path));

interface NavEntryButtonProps {
	entry: TNavEntry;
	isActive: boolean;
	onClick: () => void;
}

export const NavEntryButton: FC<NavEntryButtonProps> = ({ entry, isActive, onClick }) => {
	const button = (
		<Button
			onClick={onClick}
			sx={{
				color: isActive ? '#5aa1bf' : 'primary',
				display: 'block',
				textTransform: 'none',
				fontWeight: 600,
				whiteSpace: 'nowrap',
			}}
		>
			{entry.name}
		</Button>
	);

	if (!entry.badge) return button;

	return (
		<Badge
			badgeContent={entry.badge}
			color="error"
			sx={{
				'& .MuiBadge-badge': {
					top: '6px',
					right: '4px',
					padding: '2px 5px',
					height: 'auto',
					fontSize: '0.65rem',
					fontWeight: 600,
				},
				marginRight: '10px',
			}}
		>
			{button}
		</Badge>
	);
};

interface NavigationItemsProps {
	isMobile: boolean;
	handleCloseNavMenu: () => void;
	availableVatReportTypes: TGetAvailableStandardVatReportTypesResponseData;
	availableStandardVatReportTypes: TGetAvailableStandardVatReportTypesResponseData;
	isShipmentsEnabled: boolean;
	isEuDdpEnabled: boolean;
	location: ReturnType<typeof useLocation>;
	navigate: ReturnType<typeof useNavigate>;
}

export const NavigationItems: FC<NavigationItemsProps> = ({
	isMobile,
	handleCloseNavMenu,
	availableVatReportTypes,
	availableStandardVatReportTypes,
	isShipmentsEnabled,
	isEuDdpEnabled,
	location,
	navigate,
}) => {
	const entries = getNavEntries({
		availableVatReportTypes,
		availableStandardVatReportTypes,
		isShipmentsEnabled,
		isEuDdpEnabled,
	});

	return (
		<>
			{entries.map((entry) => (
				<NavEntryButton
					key={entry.path}
					entry={entry}
					isActive={isNavEntryActive(location.pathname, entry)}
					onClick={() => {
						navigate(entry.path);
						if (isMobile) handleCloseNavMenu();
					}}
				/>
			))}
		</>
	);
};
